/** Khoze background engine — the in-flight chat request lives HERE, not in the
 *  AssistantPage component. Leaving the assistant tab no longer orphans the
 *  request: it keeps running, other tabs show the floating "thinking" pill, and
 *  the answer lands in the thread (server-side persisted) as soon as it arrives.
 *  The engine also accumulates the PARTIAL text so a page that mounts mid-answer
 *  can render the live bubble and keep it updating via the subscription. */

export interface SourceRef { title: string; resourceId?: string; pageNumber?: number; url?: string }
export interface KhozeAnswer { threadId: string; text: string; sources: SourceRef[] }

export interface KhozeJob {
  threadId: string;
  question: string;
  status: "thinking" | "done" | "failed";
  partial: string;
  answer?: KhozeAnswer;
}

let job: KhozeJob | null = null;
const listeners = new Set<(current: KhozeJob | null) => void>();

function emit() {
  for (const listener of listeners) listener(job);
}

export function subscribeKhoze(listener: (current: KhozeJob | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function currentKhozeJob(): KhozeJob | null {
  return job;
}

export function clearKhozeJob() {
  job = null;
  emit();
}

/** Run one chat turn under engine supervision. `run` receives a notify(full)
 *  callback that MUST be called with the accumulated text on every delta — the
 *  engine records it as job.partial so pages mounting mid-answer can catch up.
 *  The returned promise resolves for the CALLER as usual; unmounting the
 *  assistant page changes nothing — state lives at module scope. */
export function askKhoze(
  threadId: string,
  question: string,
  run: (notify: (full: string) => void) => Promise<KhozeAnswer>
): Promise<KhozeAnswer> {
  job = { threadId, question, status: "thinking", partial: "" };
  emit();
  return run((full: string) => {
    if (job && job.status === "thinking") {
      job.partial = full;
      emit();
    }
  }).then(
    (answer) => {
      job = { threadId, question, status: "done", partial: answer.text, answer };
      emit();
      return answer;
    },
    (reason) => {
      job = { threadId, question, status: "failed", partial: job?.partial ?? "" };
      emit();
      throw reason;
    }
  );
}
